import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { SEO_GAME_CATALOG } from '../../data/seoGameCatalog';
import { SEO_GAME_DETAILS } from '../../data/seoGameDetails';

const CATEGORY_INFO = {
  educational: {
    name: 'Mokomieji žaidimai',
    path: '/mokomieji-zaidimai',
  },
  math: {
    name: 'Matematikos žaidimai',
    path: '/matematikos-zaidimai',
  },
  reading: {
    name: 'Skaitymo žaidimai',
    path: '/skaitymo-zaidimai',
  },
  lithuanian: {
    name: 'Lietuvių kalbos žaidimai',
    path: '/lietuviu-kalbos-zaidimai',
  },
  speech: {
    name: 'Logopediniai žaidimai',
    path: '/logopediniai-zaidimai',
  },
};

function getCategoryInfo(game) {
  const category = game.categories?.find(
    (item) => CATEGORY_INFO[item]
  );

  return CATEGORY_INFO[category] || CATEGORY_INFO.educational;
}

function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

const RELATED_GAMES_BY_SLUG = (() => {
  const topicFrequency = SEO_GAME_CATALOG.reduce((acc, item) => {
    (item.topics || []).forEach((topic) => {
      acc[topic] = (acc[topic] || 0) + 1;
    });
    return acc;
  }, {});

  const skillFrequency = SEO_GAME_CATALOG.reduce((acc, item) => {
    if (item.skill) {
      acc[item.skill] = (acc[item.skill] || 0) + 1;
    }
    return acc;
  }, {});

  const weakSlugs = new Set([
    'didziuju-raidziu-detektyvas',
    'emociju-charados',
    'geografijos-lobiu-medziokle',
    'geometrijos-statytojas',
    'grafiku-kurejas',
    'kuciu-stalas',
    'laiko-kelione-per-istorija',
    'laikrodzio-meistras',
    'matematikos-meno-kurejas',
    'mokytojo-padejejas',
    'natu-pavadinimai',
    'oru-stotis',
    'parduotuves-kasininkas',
    'pasakos-sudeliojimas',
    'receptu-matematikos-sefas',
    'skyrybos-policija',
    'svajoniu-kurejas',
    'veterinarijos-klinika',
    'virtuali-mokslo-laboratorija',
  ]);

  const semanticScore = (source, target) => {
    if (source.slug === target.slug) return -Infinity;

    const commonTopics = (source.topics || []).filter((topic) =>
      (target.topics || []).includes(topic)
    );

    const commonSkills =
      source.skill && source.skill === target.skill
        ? [source.skill]
        : [];

    const commonCategories = (source.categories || []).filter((cat) =>
      (target.categories || []).includes(cat)
    );

    if (
      commonTopics.length === 0 &&
      commonSkills.length === 0 &&
      commonCategories.length === 0
    ) {
      return -Infinity;
    }

    let score = 0;

    score += commonTopics.length * 100;
    score += commonSkills.length * 30;
    score += commonCategories.length * 10;

    commonTopics.forEach((topic) => {
      score += Math.max(
        1,
        20 - (topicFrequency[topic] || 0) * 2
      );
    });

    commonSkills.forEach((skill) => {
      score += Math.max(
        1,
        10 - (skillFrequency[skill] || 0)
      );
    });

    return score;
  };

  const candidates = {};

  SEO_GAME_CATALOG.forEach((source) => {
    candidates[source.slug] = SEO_GAME_CATALOG
      .filter((target) => {
        if (source.slug === target.slug) return false;

        return target.categories?.some((cat) =>
          source.categories?.includes(cat)
        );
      })
      .map((target) => ({
        target,
        score: semanticScore(source, target),
      }))
      .filter(({ score }) => Number.isFinite(score))
      .sort(
        (a, b) =>
          b.score - a.score ||
          a.target.name.localeCompare(b.target.name, 'lt')
      );
  });

  const selected = {};

  SEO_GAME_CATALOG.forEach((item) => {
    selected[item.slug] = [];
  });

  const inbound = {};

  SEO_GAME_CATALOG.forEach((item) => {
    inbound[item.slug] = 0;
  });

  const hasSelected = (sourceSlug, targetSlug) =>
    selected[sourceSlug].some(
      (item) => item.slug === targetSlug
    );

  const addEdge = (sourceSlug, target) => {
    if (
      selected[sourceSlug].length >= 6 ||
      hasSelected(sourceSlug, target.slug)
    ) {
      return false;
    }

    selected[sourceSlug].push(target);
    inbound[target.slug] += 1;

    return true;
  };

  // PHASE 1: guarantee minimum inbound coverage of 5.

  while (
    SEO_GAME_CATALOG.some((item) => inbound[item.slug] < 5)
  ) {
    const undercovered = SEO_GAME_CATALOG
      .filter((item) => inbound[item.slug] < 5)
      .map((target) => {
        const possibleSources = SEO_GAME_CATALOG.filter(
          (source) =>
            source.slug !== target.slug &&
            selected[source.slug].length < 6 &&
            candidates[source.slug].some(
              ({ target: candidate }) =>
                candidate.slug === target.slug
            )
        ).length;

        return {
          target,
          possibleSources,
        };
      })
      .sort(
        (a, b) =>
          a.possibleSources - b.possibleSources ||
          a.target.name.localeCompare(b.target.name, 'lt')
      );

    if (undercovered.length === 0) {
      break;
    }

    const target = undercovered[0].target;

    const possibleSources = SEO_GAME_CATALOG
      .filter(
        (source) =>
          source.slug !== target.slug &&
          selected[source.slug].length < 6 &&
          !hasSelected(source.slug, target.slug) &&
          candidates[source.slug].some(
            ({ target: candidate }) =>
              candidate.slug === target.slug
          )
      )
      .sort((a, b) => {
        const aScore =
          candidates[a.slug].find(
            ({ target: candidate }) =>
              candidate.slug === target.slug
          )?.score ?? -Infinity;

        const bScore =
          candidates[b.slug].find(
            ({ target: candidate }) =>
              candidate.slug === target.slug
          )?.score ?? -Infinity;

        const aWeak = weakSlugs.has(a.slug) ? 1 : 0;
        const bWeak = weakSlugs.has(b.slug) ? 1 : 0;

        return (
          bWeak - aWeak ||
          bScore - aScore ||
          selected[a.slug].length -
            selected[b.slug].length ||
          a.name.localeCompare(b.name, 'lt')
        );
      });

    if (possibleSources.length === 0) {
      break;
    }

    addEdge(possibleSources[0].slug, target);
  }

  // PHASE 2: fill every source to exactly 6.

  SEO_GAME_CATALOG.forEach((source) => {
    const sourceCandidates = candidates[source.slug];

    while (selected[source.slug].length < 6) {
      const available = sourceCandidates
        .filter(
          ({ target }) =>
            !hasSelected(source.slug, target.slug) &&
            inbound[target.slug] < 14
        )
        .map(({ target, score }) => {
          const coverageBonus =
            inbound[target.slug] < 5
              ? 1000
              : inbound[target.slug] < 6
                ? 300
                : inbound[target.slug] < 7
                  ? 100
                  : 0;

          const balanceBonus =
            Math.max(0, 10 - inbound[target.slug]) * 8;

          const weakBonus =
            weakSlugs.has(target.slug) &&
            inbound[target.slug] < 8
              ? 120
              : 0;

          return {
            target,
            score:
              score +
              coverageBonus +
              balanceBonus +
              weakBonus,
          };
        })
        .sort(
          (a, b) =>
            b.score - a.score ||
            inbound[a.target.slug] -
              inbound[b.target.slug] ||
            a.target.name.localeCompare(b.target.name, 'lt')
        );

      if (available.length === 0) {
        break;
      }

      addEdge(source.slug, available[0].target);
    }
  });

  // SAFETY PASS: strict same-category only.

  SEO_GAME_CATALOG.forEach((source) => {
    if (selected[source.slug].length >= 6) return;

    const fallback = SEO_GAME_CATALOG
      .filter(
        (target) =>
          target.slug !== source.slug &&
          !hasSelected(source.slug, target.slug) &&
          target.categories?.some((cat) =>
            source.categories?.includes(cat)
          )
      )
      .sort(
        (a, b) =>
          inbound[a.slug] -
            inbound[b.slug] ||
          a.name.localeCompare(b.name, 'lt')
      );

    for (const target of fallback) {
      if (selected[source.slug].length >= 6) {
        break;
      }

      addEdge(source.slug, target);
    }
  });

  const graph = {};

  SEO_GAME_CATALOG.forEach((source) => {
    graph[source.slug] = selected[source.slug].slice(0, 6);
  });

  return graph;
})();

export default function SEOGamePage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const game = SEO_GAME_CATALOG.find(
    (item) => item.slug === slug
  );

  if (!game) {
    return (
      <>
        <SEO
          title="Žaidimas nerastas | SmartPanda"
          description="Ieškomas SmartPanda mokomasis žaidimas nerastas."
          canonical={`/zaidimai/${slug || ''}`}
          noindex
        />

        <main className="min-h-screen flex items-center justify-center px-6 py-20">
          <div className="max-w-xl text-center">
            <div className="text-6xl mb-6">🔎</div>

            <h1 className="text-4xl font-black text-slate-900 mb-4">
              Žaidimas nerastas
            </h1>

            <p className="text-lg text-slate-600 mb-8">
              Šis mokomasis žaidimas šiuo metu nerastas.
            </p>

            <Link
              to="/mokomieji-zaidimai"
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-indigo-600 text-white font-black hover:bg-indigo-700 transition"
            >
              Peržiūrėti mokomuosius žaidimus
            </Link>
          </div>
        </main>
      </>
    );
  }

  const category = getCategoryInfo(game);

  const details =
    SEO_GAME_DETAILS[game.slug] ||
    SEO_GAME_DETAILS[game.id] ||
    {};

  const intro =
    details.intro ||
    `„${game.name}“ – interaktyvus mokomasis žaidimas vaikams, padedantis lavinti ${game.skill || 'įvairius mokymosi įgūdžius'}.`;

  const mechanics =
    details.mechanics ||
    'Žaidime vaikas atlieka interaktyvias užduotis, pasirenka atsakymus ir gauna grįžtamąjį ryšį.';

  const howToPlay =
    details.howToPlay ||
    'Perskaitykite užduotį, atlikite pateiktą veiksmą ir pasirinkite arba įveskite atsakymą.';

  const benefits = unique([
    ...(details.benefits || []),
    game.skill,
    ...(game.topics || []),
  ]);

  const suitableFor =
    details.suitableFor ||
    'Tinka vaikams, kurie mokosi per žaidimą ir interaktyvias užduotis.';

  const topics = unique([
    ...(game.topics || []),
    game.skill,
  ]);

  const TOPIC_HUB_LINKS = {
    'Loginis mąstymas': '/loginis-mastymas',
    'Skaičiai ir skaičiavimas': '/skaiciai-ir-skaiciavimas',
    'Žodynas': '/zodynas',
    'Teksto supratimas': '/teksto-supratimas',
    'Dėmesys': '/demesys',
    'Gamtos mokslai': '/gamtos-mokslu-zaidimai',
    'Kūrybiškumas': '/kurybiniai-zaidimai',
  };

  const relatedGames = RELATED_GAMES_BY_SLUG[game.slug] || [];

  const playPath = `/ai-games?game=${encodeURIComponent(game.id)}`;
  const loginPath = `/login?redirect=${encodeURIComponent(playPath)}`;

  const description = `„${game.name}“ – ${intro.replace(/^„.*?“\s*–\s*/u, '')}`.slice(
    0,
    158
  );

  const breadcrumbs = [
    {
      name: 'Pradžia',
      url: '/',
    },
    {
      name: category.name,
      url: category.path,
    },
    {
      name: game.name,
      url: `/zaidimai/${game.slug}`,
    },
  ];

  return (
    <>
      <SEO
        title={`${game.name} – mokomasis žaidimas vaikams | SmartPanda`}
        description={description}
        canonical={`/zaidimai/${game.slug}`}
        about={topics}
        learningOutcomes={benefits}
        pageType="LearningResource"
        breadcrumbs={breadcrumbs}
      />

      <main className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
        {/* Breadcrumbs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <nav
            aria-label="Breadcrumb"
            className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-500"
          >
            <Link
              to="/"
              className="hover:text-indigo-600 transition"
            >
              Pradžia
            </Link>

            <span>›</span>

            <Link
              to={category.path}
              className="hover:text-indigo-600 transition"
            >
              {category.name}
            </Link>

            <span>›</span>

            <span className="text-slate-800">
              {game.name}
            </span>
          </nav>
        </div>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-16">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 text-indigo-700 text-sm font-black mb-5">
                🎮 {category.name}
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-slate-950 leading-tight mb-6">
                {game.name}
              </h1>

              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-5">
                {intro}
              </p>

              <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-8">
                {mechanics}
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate(loginPath)}
                  className="inline-flex items-center justify-center px-7 py-4 rounded-2xl bg-indigo-600 text-white font-black text-lg shadow-lg hover:bg-indigo-700 hover:-translate-y-0.5 transition"
                >
                  🎮 Žaisti žaidimą
                </button>

                <Link
                  to={category.path}
                  className="inline-flex items-center justify-center px-7 py-4 rounded-2xl bg-white border-2 border-slate-200 text-slate-700 font-black text-lg hover:border-indigo-300 hover:text-indigo-700 transition"
                >
                  ← Visi žaidimai
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-[2rem] bg-gradient-to-br from-indigo-100 via-white to-purple-100 border border-indigo-100 shadow-xl p-8 md:p-12 text-center">
                <div className="text-7xl md:text-8xl mb-6">
                  🧠
                </div>

                <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-3">
                  Mokomės žaisdami
                </h2>

                <p className="text-slate-600 leading-relaxed">
                  SmartPanda interaktyvios užduotys padeda vaikams
                  praktiškai pritaikyti žinias ir mokytis per veiksmą.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Learning outcomes */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid md:grid-cols-2 gap-6">
            <article className="rounded-3xl bg-white border border-slate-200 shadow-sm p-7 md:p-8">
              <div className="text-3xl mb-4">🎯</div>

              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-5">
                Ko vaikas mokosi?
              </h2>

              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <li
                    key={`${benefit}-${index}`}
                    className="flex items-start gap-3 text-slate-700 font-semibold"
                  >
                    <span className="mt-1 text-emerald-500">
                      ✓
                    </span>

                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </article>

            <article className="rounded-3xl bg-white border border-slate-200 shadow-sm p-7 md:p-8">
              <div className="text-3xl mb-4">📚</div>

              <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-5">
                Kaip žaisti?
              </h2>

              <p className="text-slate-600 text-lg leading-relaxed mb-5">
                {howToPlay}
              </p>

              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-5">
                <p className="text-sm font-black uppercase tracking-wider text-slate-400 mb-2">
                  Kam tinka?
                </p>

                <p className="text-slate-700 font-semibold leading-relaxed">
                  {suitableFor}
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Topics */}
        {topics.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            <div className="rounded-3xl bg-slate-900 text-white p-7 md:p-10">
              <div className="text-3xl mb-4">🔎</div>

              <h2 className="text-2xl md:text-3xl font-black mb-6">
                Žaidimo temos
              </h2>

              <div className="flex flex-wrap gap-3">
                {topics.map((topic, index) => {
                  const hubPath = TOPIC_HUB_LINKS[topic];

                  if (hubPath) {
                    return (
                      <Link
                        key={`${topic}-${index}`}
                        to={hubPath}
                        className="px-4 py-2 rounded-full bg-white/10 border border-white/10 text-slate-200 font-bold hover:bg-white/20 transition-colors"
                      >
                        {topic}
                      </Link>
                    );
                  }

                  return (
                    <span
                      key={`${topic}-${index}`}
                      className="px-4 py-2 rounded-full bg-white/10 border border-white/10 text-slate-200 font-bold"
                    >
                      {topic}
                    </span>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Related games */}
        {relatedGames.length > 0 && (
          <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <p className="text-sm font-black uppercase tracking-widest text-indigo-500 mb-2">
                  Toliau mokykis
                </p>

                <h2 className="text-3xl md:text-4xl font-black text-slate-900">
                  Susiję žaidimai
                </h2>
              </div>

              <Link
                to={category.path}
                className="hidden sm:block text-indigo-600 font-black hover:text-indigo-800"
              >
                Visi žaidimai →
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedGames.map((related) => (
                <Link
                  key={related.slug}
                  to={`/zaidimai/${related.slug}`}
                  className="group rounded-2xl bg-white border border-slate-200 p-6 shadow-sm hover:shadow-lg hover:border-indigo-200 hover:-translate-y-1 transition"
                >
                  <div className="text-3xl mb-4">🎮</div>

                  <h3 className="text-xl font-black text-slate-900 group-hover:text-indigo-600 transition">
                    {related.name}
                  </h3>

                  <p className="text-sm text-slate-500 font-semibold mt-2">
                    {related.skill}
                  </p>

                  <span className="inline-block mt-5 text-sm font-black text-indigo-600">
                    Peržiūrėti žaidimą →
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 pb-20">
          <div className="rounded-[2rem] bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-8 md:p-12 text-center shadow-xl">
            <h2 className="text-3xl md:text-4xl font-black mb-4">
              Pasiruošę pradėti?
            </h2>

            <p className="text-lg text-indigo-100 max-w-2xl mx-auto mb-7">
              Išbandykite „{game.name}“ ir paverskite mokymąsi
              interaktyvia žaidybine patirtimi.
            </p>

            <button
              type="button"
              onClick={() => navigate(loginPath)}
              className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-white text-indigo-700 font-black text-lg hover:bg-indigo-50 transition"
            >
              🎮 Pradėti žaisti
            </button>
          </div>
        </section>
      </main>
    </>
  );
}
